import React, { useState, useEffect } from 'react';
import { Box, Chip, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MDButton } from '../MDButton';
import { MDTypography } from 'components/MDTypography';

const useStyles = makeStyles((theme) => ({
  formatContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(2),
    backgroundColor: '#f9f9f9',
  },
  chip: {
    margin: theme.spacing(0.5),
    cursor: 'pointer',
  },
  preview: {
    backgroundColor: '#fff',
    padding: theme.spacing(1),
    borderRadius: 4,
    border: '1px solid #ddd',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  availableFields: {
    marginBottom: theme.spacing(2),
  },
  currentFormat: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export interface BillNameFormatProps {
  value: string;
  onChange: (format: string) => void;
}

interface FormatField {
  key: string;
  label: string;
  example: string;
}

const AVAILABLE_FIELDS: FormatField[] = [
  { key: 'caseId', label: 'Case ID', example: 'CASE123' },
  { key: 'billId', label: 'Bill ID', example: 'BILL456' },
  { key: 'policyNumber', label: 'Policy Number', example: 'POL789' },
  // { key: 'patientType', label: 'Patient Type', example: 'Outpatient' },
  { key: 'billType', label: 'Bill Type', example: 'Professional' },
];

export const BillNameFormat: React.FC<BillNameFormatProps> = ({
  value,
  onChange,
}) => {
  const classes = useStyles();
  const [currentFormat, setCurrentFormat] = useState(value);

  useEffect(() => {
    setCurrentFormat(value);
  }, [value]);

  const handleAddField = (field: FormatField) => {
    const newFormat = currentFormat + `{${field.key}}`;
    setCurrentFormat(newFormat);
    onChange(newFormat);
  };

  const handleAddSeparator = (separator: string) => {
    const newFormat = currentFormat + separator;
    setCurrentFormat(newFormat);
    onChange(newFormat);
  };

  const handleReset = () => {
    const defaultFormat = '{caseId}_{billId}';
    setCurrentFormat(defaultFormat);
    onChange(defaultFormat);
  };

  const handleClear = () => {
    setCurrentFormat('');
    onChange('');
  };

  const generatePreview = (format: string): string => {
    if (!format) return 'No format specified';

    let preview = format;
    AVAILABLE_FIELDS.forEach((field) => {
      preview = preview.replace(
        new RegExp(`\\{${field.key}\\}`, 'g'),
        field.example
      );
    });
    return preview + '.pdf';
  };

  return (
    <Box className={classes.formatContainer}>
      <MDTypography variant="h6" gutterBottom>
        Bill File Name Format
      </MDTypography>

      <MDTypography variant="caption" color="dark" gutterBottom>
        Click on the fields below to add them to your filename format. You can
        combine multiple fields with separators.
      </MDTypography>

      <Box className={classes.availableFields}>
        <Grid container spacing={1} style={{ justifyContent: 'flex-start' }}>
          {AVAILABLE_FIELDS.map((field) => (
            <Grid item key={field.key}>
              <Chip
                label={field.label}
                onClick={() => handleAddField(field)}
                className={classes.chip}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box mb={2}>
        <MDTypography variant="subtitle2" gutterBottom>
          Separators:
        </MDTypography>
        <Grid container spacing={1}>
          {['_', '-', '.', ' '].map((separator) => (
            <Grid item key={separator}>
              <Chip
                label={separator === ' ' ? 'Space' : separator}
                onClick={() => handleAddSeparator(separator)}
                className={classes.chip}
                variant="outlined"
                size="small"
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box className={classes.currentFormat}>
        <MDTypography variant="subtitle2" gutterBottom>
          Current Format:
        </MDTypography>
        <Box className={classes.preview}>
          {currentFormat || 'No format specified'}
        </Box>
      </Box>

      <Box mb={2}>
        <MDTypography variant="subtitle2" gutterBottom>
          Preview:
        </MDTypography>
        <Box className={classes.preview}>{generatePreview(currentFormat)}</Box>
      </Box>

      <Box display="flex" style={{ gap: '8px' }}>
        <MDButton
          variant="outlined"
          color="secondary"
          size="small"
          onClick={handleReset}
        >
          Reset to Default
        </MDButton>
        <MDButton
          color="white"
          variant="outlined"
          style={{ color: 'black', borderColor: 'black' }}
          size="small"
          onClick={handleClear}
        >
          Clear
        </MDButton>
      </Box>
    </Box>
  );
};
